import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import axios from 'axios';
import { getApiUrl } from '../services/api';
import PhaseTimeline, { PHASES, PHASE_COLOR } from '../components/opportunities/PhaseTimeline';
import ContactsPanel from '../components/opportunities/ContactsPanel';
import JournalPanel from '../components/opportunities/JournalPanel';
import TasksPanel from '../components/opportunities/TasksPanel';
import { exportOpportunityToPdf } from '../services/opportunityPdfExport';
import {
  ArrowLeft, Edit2, Check, X, ChevronRight, FileText, Users, BookOpen,
  CheckSquare, Paperclip, MapPin, Target, AlertCircle, Loader, Search,
  ClipboardList, Building2, FileSignature, Star, Trash2, Image as ImageIcon
} from 'lucide-react';

// Build an absolute URL for an uploaded file. getApiUrl() ends in "/api"; the
// static /uploads route is served from the API origin (one level up from /api).
const fileUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//.test(url)) return url;
  const base = getApiUrl().replace(/\/api\/?$/, '');
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
};

const isImage = (att) => /image\//.test(att?.type || '') || /\.(png|jpe?g|gif|webp)$/i.test(att?.name || '');

// Catastro "planta baja" (ground floor) codes.
const GROUND_FLOOR = new Set(['00', 'PB', 'BJ', 'BA']);
const isGroundFloor = (planta) => GROUND_FLOOR.has(String(planta || '').trim().toUpperCase());

// Extract the ground-floor units from the Catastro `inmuebles[]` payload.
const groundFloorUnits = (inmuebles = []) => {
  const units = [];
  for (const inm of inmuebles) {
    const gf = (inm.construcciones || []).filter(c => isGroundFloor(c.planta));
    if (gf.length === 0) continue;
    const m2 = gf.reduce((s, c) => s + (Number(c.superficieM2) || 0), 0) || Number(inm.superficieConstruccionM2) || 0;
    units.push({
      rc: inm.referenciaCatastral,
      uso: inm.uso || gf[0]?.uso || '',
      direccion: inm.direccion || '',
      m2,
      puertas: gf.map(c => c.puerta).filter(Boolean).join(', '),
    });
  }
  return units;
};

// Prepend a line to a notes textarea, avoiding duplicates.
const prependNote = (notes, line) => {
  const base = (notes || '').trim();
  if (base.includes(line)) return base;
  return base ? `${line}\n${base}` : line;
};

const PHASE_LABELS = Object.fromEntries(PHASES.map(p => [p.key, p.label]));

const PASTA_FIELDS = [
  { key: 'parking',    label: 'Parking' },
  { key: 'access',     label: 'Acceso' },
  { key: 'signing',    label: 'Señalización' },
  { key: 'traffic',    label: 'Tráfico' },
  { key: 'activity',   label: 'Actividad' },
  { key: 'visibility', label: 'Visibilidad' },
];

const TABS = [
  { key: 'summary',   label: 'Resumen',    Icon: FileText },
  { key: 'general',   label: 'General',    Icon: ClipboardList },
  { key: 'local',     label: 'Local',      Icon: Building2 },
  { key: 'data',      label: 'Dirección',  Icon: MapPin },
  { key: 'contract',  label: 'Contrato',   Icon: FileSignature },
  { key: 'contacts',  label: 'Contactos',  Icon: Users },
  { key: 'journal',   label: 'Diario',     Icon: BookOpen },
  { key: 'tasks',     label: 'Tareas',     Icon: CheckSquare },
  { key: 'documents', label: 'Documentos', Icon: Paperclip },
];

// ── Opciones de los desplegables (reflejan el flujo de apertura de local) ──────
const OPT = {
  pais:            ['España', 'Portugal'],
  formato:         ['Inline', 'Freestanding', 'FoodCourt', 'Drive-Through Only', 'Foodtrack'],
  marca:           ['BK', 'Popeyes', 'Tim Hortons', 'Firehouse Subs', 'Otros'],
  tipologia:       ['Calle', 'Calle Prime', 'C.Comercial', 'Parque de Medianas', 'Gasolinera', 'Carretera', 'Estación', 'Aeropuerto', 'Otros'],
  localTipo:       ['Local', 'Parcela'],
  tipoDocumento:   ['Nuevo contrato', 'Renovación de contrato', 'Carta de Intenciones', 'Adenda'],
  tipoFirma:       ['Firma digital', 'Firma física'],
  tipoContrato:    ['Contrato de derecho de superficie', 'Contrato de arrendamiento de suelo', 'Contrato de arrendamiento de local'],
  calidadFacultado:['Administrador Único', 'Apoderado'],
};

// Field specs → drive the generic MetadataForm. type: text|number|textarea|select
const GENERAL_SECTIONS = [
  { title: 'Información general', fields: [
    { key: 'pais',        label: 'País',                  type: 'select', options: OPT.pais },
    { key: 'formato',     label: 'Formato',               type: 'select', options: OPT.formato },
    { key: 'marca',       label: 'Marca',                 type: 'select', options: OPT.marca },
    { key: 'tipologia',   label: 'Tipología',             type: 'select', options: OPT.tipologia },
    { key: 'responsable', label: 'Responsable expansión', type: 'text' },
    { key: 'superficie',  label: 'Superficie local/parcela (m²)', type: 'number' },
    { key: 'rentaEstimada', label: 'Renta estimada (€)',  type: 'number' },
    { key: 'descripcion', label: 'Descripción',           type: 'textarea', span: 2, maxLength: 4000 },
  ]},
];

const LOCAL_SECTIONS = [
  { title: 'Superficies', fields: [
    { key: 'tipo',            label: 'Tipo',                   type: 'select', options: OPT.localTipo },
    { key: 'localM2',         label: 'Local (m²)',             type: 'number' },
    { key: 'plantaCalle',     label: 'Planta calle (m²)',      type: 'number' },
    { key: 'plantaPrimera',   label: 'Planta primera (m²)',    type: 'number' },
    { key: 'plantaSotano',    label: 'Planta sótano (m²)',     type: 'number' },
    { key: 'terraza',         label: 'Terraza (m²)',           type: 'number' },
    { key: 'otrosPuntual',    label: 'Otros puntual (m²)',     type: 'number' },
    { key: 'superficieTotal', label: 'Superficie total local (m²)', type: 'number' },
  ]},
];

const CONTRACT_SECTIONS = [
  { title: 'Documento y firma', fields: [
    { key: 'tipoDocumento',    label: 'Tipo de documento',  type: 'select', options: OPT.tipoDocumento },
    { key: 'tipoFirma',        label: 'Tipo de firma',      type: 'select', options: OPT.tipoFirma },
    { key: 'tipoContrato',     label: 'Tipo de contrato',   type: 'select', options: OPT.tipoContrato },
    { key: 'calidadFacultado', label: 'Calidad de facultado', type: 'select', options: OPT.calidadFacultado },
    { key: 'ibanArrendadora',  label: 'IBAN arrendadora',   type: 'text' },
    { key: 'revisionAnualidad',label: 'Revisión anualidad', type: 'text' },
    { key: 'duracion',         label: 'Duración (años)',    type: 'text', maxLength: 255 },
  ]},
  { title: 'Cláusulas', fields: [
    { key: 'obligadoCumplimiento',      label: 'Obligado cumplimiento',            type: 'textarea', maxLength: 255 },
    { key: 'subarriendo',               label: 'Subarriendo',                      type: 'textarea', maxLength: 255 },
    { key: 'autorizacionObras',         label: 'Autorización obras',               type: 'textarea', maxLength: 255 },
    { key: 'autorizacionMaquinaCubierta', label: 'Autorización máquina en cubierta', type: 'textarea', maxLength: 255 },
    { key: 'autorizacionCambioTitular', label: 'Autorización cambio titular suministro', type: 'textarea', maxLength: 255 },
    { key: 'salidasAnticipadas',        label: 'Salidas anticipadas',              type: 'textarea', maxLength: 255 },
    { key: 'devengoRenta',              label: 'Devengo de renta',                 type: 'textarea', maxLength: 255 },
    { key: 'bonificacionFuerzaMayor',   label: 'Bonificación fuerza mayor (covid)', type: 'textarea', maxLength: 255 },
    { key: 'cambioControl',             label: 'Cambio control',                   type: 'textarea', maxLength: 255 },
    { key: 'cesion',                    label: 'Cesión',                           type: 'textarea', maxLength: 255 },
    { key: 'entregaParcela',            label: 'Entrega de la parcela',            type: 'textarea', maxLength: 255 },
  ]},
];

const PROPERTY_FEATURE_OPTIONS = [
  'Salida de humos',
  'Acceso minusvalidos',
  'Terraza',
  'Licencia de actividad',
  'Instalacion electrica actualizada',
  'Acometida de gas',
  'Almacen',
  'Montacargas',
  'Ascensor',
  'Vado permanente'
];

const OpportunityDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('summary');
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [advancingPhase, setAdvancingPhase] = useState(false);
  const [phaseNote, setPhaseNote] = useState('');
  const [showPhaseModal, setShowPhaseModal] = useState(false);
  const [pendingPhase, setPendingPhase] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchOpportunity = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${getApiUrl()}/opportunities/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOpportunity(data);
      setNameValue(data.name);
    } catch (e) {
      setError('No se pudo cargar la oportunidad.');
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => { fetchOpportunity(); }, [fetchOpportunity]);

  const handleSaveName = async () => {
    if (!nameValue.trim()) return;
    try {
      const { data } = await axios.put(
        `${getApiUrl()}/opportunities/${id}`,
        { name: nameValue.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOpportunity(data);
      setEditingName(false);
    } catch (e) {
      console.error('Error saving name', e);
    }
  };

  const handlePhaseSelect = (phase) => {
    if (phase === opportunity.phase) return;
    setPendingPhase(phase);
    setPhaseNote('');
    setShowPhaseModal(true);
  };

  const confirmPhaseChange = async () => {
    setAdvancingPhase(true);
    try {
      const { data } = await axios.put(
        `${getApiUrl()}/opportunities/${id}/phase`,
        { phase: pendingPhase, notes: phaseNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOpportunity(data);
      setShowPhaseModal(false);
    } catch (e) {
      console.error('Error changing phase', e);
    } finally {
      setAdvancingPhase(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      await exportOpportunityToPdf(opportunity);
    } finally {
      setExporting(false);
    }
  };

  const updateOpportunityMetadata = async (metadataPatch) => {
    const nextMetadata = {
      ...(opportunity.metadata || {}),
      ...metadataPatch
    };

    const { data } = await axios.put(
      `${getApiUrl()}/opportunities/${id}`,
      { metadata: nextMetadata },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setOpportunity(data);
  };

  const handleTogglePropertyFeature = async (feature) => {
    try {
      const current = opportunity?.metadata?.propertyFeatures || [];
      const exists = current.includes(feature);
      const next = exists
        ? current.filter(f => f !== feature)
        : [...current, feature];
      await updateOpportunityMetadata({ propertyFeatures: next });
    } catch (e) {
      console.error('Error updating property features', e);
    }
  };

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#05070A', color: '#333', fontFamily: 'monospace', letterSpacing: '2px' }}>
        CARGANDO FICHA...
      </div>
    );
  }

  if (error || !opportunity) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#05070A', gap: '16px' }}>
        <AlertCircle size={40} color="#E74C3C" />
        <p style={{ color: '#E74C3C' }}>{error || 'Oportunidad no encontrada'}</p>
        <button onClick={() => navigate('/opportunities')} style={{ background: '#E27B58', border: 'none', borderRadius: '6px', color: 'white', padding: '10px 20px', cursor: 'pointer' }}>
          Volver
        </button>
      </div>
    );
  }

  const phase = opportunity.phase || 'exploration';
  const phaseColor = PHASE_COLOR[phase] || '#E27B58';
  const currentPhaseIndex = PHASES.findIndex(p => p.key === phase);
  const nextPhase = PHASES[currentPhaseIndex + 1];

  const coords = opportunity.location?.coordinates;
  const lat = coords ? coords[1] : null;
  const lng = coords ? coords[0] : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#05070A', color: '#F0F0F0', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(5,7,10,0.98)',
        flexShrink: 0,
      }}>
        {/* Breadcrumb + actions row */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
          <button onClick={() => navigate('/opportunities')} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#888', cursor: 'pointer', padding: '6px', display: 'flex' }}>
            <ArrowLeft size={15} />
          </button>
          <span style={{ fontSize: '0.7rem', color: '#444', fontFamily: 'monospace' }}>
            Opportunities
          </span>
          <ChevronRight size={12} color="#333" />
          <span style={{ fontSize: '0.7rem', color: '#888', fontFamily: 'monospace', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {opportunity.name}
          </span>

          <div style={{ flex: 1 }} />

          {/* Export PDF */}
          <button
            onClick={handleExportPdf}
            disabled={exporting}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: 'transparent',
              border: '1px solid rgba(0,206,201,0.4)',
              borderRadius: '6px',
              color: '#00CEC9',
              cursor: 'pointer',
              padding: '7px 14px',
              fontSize: '0.78rem',
              fontWeight: '600',
            }}
          >
            {exporting ? <Loader size={12} /> : <FileText size={12} />}
            Exportar PDF
          </button>

          {/* Advance phase */}
          {nextPhase && (
            <button
              onClick={() => handlePhaseSelect(nextPhase.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: `${nextPhase.color}18`,
                border: `1px solid ${nextPhase.color}55`,
                borderRadius: '6px',
                color: nextPhase.color,
                cursor: 'pointer',
                padding: '7px 14px',
                fontSize: '0.78rem',
                fontWeight: '700',
              }}
            >
              Avanzar a {nextPhase.label} →
            </button>
          )}
        </div>

        {/* Name (editable) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <Target size={18} color={phaseColor} />
          {editingName ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <input
                autoFocus
                value={nameValue}
                onChange={e => setNameValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') { setEditingName(false); setNameValue(opportunity.name); } }}
                style={{
                  background: 'transparent',
                  border: `1px solid ${phaseColor}`,
                  borderRadius: '6px',
                  color: '#F0F0F0',
                  fontSize: '1.4rem',
                  fontWeight: '900',
                  padding: '4px 10px',
                  outline: 'none',
                  flex: 1,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontFamily: 'monospace',
                }}
              />
              <button onClick={handleSaveName} style={{ background: '#27AE60', border: 'none', borderRadius: '5px', color: 'white', cursor: 'pointer', padding: '6px 10px' }}><Check size={14} /></button>
              <button onClick={() => { setEditingName(false); setNameValue(opportunity.name); }} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '5px', color: '#888', cursor: 'pointer', padding: '6px 10px' }}><X size={14} /></button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
              <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: '900', color: '#F0F0F0', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: 'monospace' }}>
                {opportunity.name}
              </h1>
              <button onClick={() => setEditingName(true)} style={{ background: 'transparent', border: 'none', color: '#444', cursor: 'pointer', padding: '4px', borderRadius: '4px' }}
                onMouseOver={e => { e.currentTarget.style.color = '#E27B58'; }}
                onMouseOut={e => { e.currentTarget.style.color = '#444'; }}
              >
                <Edit2 size={14} />
              </button>
            </div>
          )}

          {/* Phase badge */}
          <span style={{
            fontSize: '0.72rem',
            color: phaseColor,
            background: `${phaseColor}18`,
            border: `1px solid ${phaseColor}44`,
            padding: '4px 12px',
            borderRadius: '20px',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            flexShrink: 0,
          }}>
            {PHASE_LABELS[phase] || phase}
          </span>
        </div>

        {/* Phase Timeline */}
        <PhaseTimeline currentPhase={phase} onSelectPhase={handlePhaseSelect} compact />
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(5,7,10,0.95)',
        flexShrink: 0,
        overflowX: 'auto',
      }}>
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '12px 20px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === key ? `2px solid ${phaseColor}` : '2px solid transparent',
              color: activeTab === key ? phaseColor : '#555',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: activeTab === key ? '700' : '400',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s',
            }}
          >
            <Icon size={13} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px', maxWidth: 720, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>

        {activeTab === 'summary' && (
          <div>
            {/* Representative photo + key general data */}
            {opportunity.metadata?.local?.representativeImage && (
              <img
                src={fileUrl(opportunity.metadata.local.representativeImage)}
                alt="Imagen representativa del local"
                style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)', marginBottom: '24px' }}
              />
            )}
            {(() => {
              const g = opportunity.metadata?.general || {};
              const rows = [
                ['Marca', g.marca], ['Formato', g.formato], ['Tipología', g.tipologia],
                ['Superficie', g.superficie && `${g.superficie} m²`], ['Renta estimada', g.rentaEstimada && `${g.rentaEstimada} €`],
                ['Responsable', g.responsable],
              ].filter(([, v]) => v);
              if (rows.length === 0) return null;
              return (
                <Section title="Ficha general" color={phaseColor}>
                  {rows.map(([label, value]) => <InfoRow key={label} label={label} value={value} />)}
                  {g.descripcion && <p style={{ margin: '8px 2px 0', fontSize: '0.85rem', color: '#CCC', lineHeight: 1.6 }}>{g.descripcion}</p>}
                </Section>
              );
            })()}

            {/* Location */}
            <Section title="Ubicación" color={phaseColor}>
              <InfoRow
                label="Dirección"
                value={opportunity.address || <em style={{ color: '#444' }}>Sin dirección — añádela en la pestaña Dirección</em>}
              />
              {lat && lng && (
                <InfoRow label="Coordenadas" value={`${lat.toFixed(6)}°N, ${lng.toFixed(6)}°E`} />
              )}
              {opportunity.country && (
                <InfoRow label="País / Región" value={opportunity.country} />
              )}
            </Section>

            {/* Phase history */}
            {opportunity.phaseHistory && opportunity.phaseHistory.length > 0 && (
              <Section title="Historial de fases" color={phaseColor}>
                {[...opportunity.phaseHistory].reverse().map((h, i) => {
                  const c = PHASE_COLOR[h.phase] || '#555';
                  return (
                    <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'flex-start' }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: c, marginTop: 5, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: '0.78rem', color: c, fontWeight: '600', textTransform: 'uppercase' }}>
                          {PHASE_LABELS[h.phase] || h.phase}
                        </div>
                        <div style={{ fontSize: '0.65rem', color: '#444', fontFamily: 'monospace' }}>
                          {new Date(h.changedAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                        {h.notes && <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '2px' }}>{h.notes}</div>}
                      </div>
                    </div>
                  );
                })}
              </Section>
            )}

            {/* PASTA V — editable */}
            <PastaScoresCard
              opportunity={opportunity}
              token={token}
              onUpdate={setOpportunity}
              phaseColor={phaseColor}
            />

            {/* Notes */}
            {opportunity.metadata?.notes && (
              <Section title="Notas" color={phaseColor}>
                <p style={{ margin: 0, fontSize: '0.88rem', color: '#CCC', lineHeight: '1.6' }}>{opportunity.metadata.notes}</p>
              </Section>
            )}

            {/* Property features selector */}
            <Section title="Caracteristicas del local" color={phaseColor}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                {PROPERTY_FEATURE_OPTIONS.map(feature => {
                  const checked = (opportunity?.metadata?.propertyFeatures || []).includes(feature);
                  return (
                    <label
                      key={feature}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${checked ? `${phaseColor}66` : 'rgba(255,255,255,0.08)'}`,
                        background: checked ? `${phaseColor}14` : 'rgba(255,255,255,0.02)',
                        cursor: 'pointer',
                        fontSize: '0.82rem',
                        color: checked ? '#F0F0F0' : '#AAA',
                        userSelect: 'none'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleTogglePropertyFeature(feature)}
                        style={{ accentColor: phaseColor }}
                      />
                      <span>{feature}</span>
                    </label>
                  );
                })}
              </div>
            </Section>

            {/* Phase tools placeholder */}
            <Section title={`Herramientas — ${PHASE_LABELS[phase] || phase}`} color={phaseColor}>
              <div style={{
                padding: '20px',
                border: '1px dashed rgba(255,255,255,0.08)',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#333',
                fontSize: '0.8rem',
              }}>
                Las herramientas específicas para la fase <strong style={{ color: phaseColor }}>{PHASE_LABELS[phase]}</strong> se diseñarán en próximas iteraciones.
              </div>
            </Section>
          </div>
        )}

        {activeTab === 'general' && (
          <MetadataForm
            opportunity={opportunity}
            token={token}
            onUpdate={setOpportunity}
            phaseColor={phaseColor}
            group="general"
            sections={GENERAL_SECTIONS}
          />
        )}

        {activeTab === 'local' && (
          <LocalTab
            opportunity={opportunity}
            token={token}
            onUpdate={setOpportunity}
            phaseColor={phaseColor}
          />
        )}

        {activeTab === 'contract' && (
          <MetadataForm
            opportunity={opportunity}
            token={token}
            onUpdate={setOpportunity}
            phaseColor={phaseColor}
            group="contract"
            sections={CONTRACT_SECTIONS}
          />
        )}

        {activeTab === 'data' && (
          <DataTab
            opportunity={opportunity}
            token={token}
            onUpdate={setOpportunity}
            phaseColor={phaseColor}
          />
        )}

        {activeTab === 'contacts' && (
          <ContactsPanel
            opportunity={opportunity}
            token={token}
            onUpdate={setOpportunity}
          />
        )}

        {activeTab === 'journal' && (
          <JournalPanel
            opportunity={opportunity}
            token={token}
            onUpdate={setOpportunity}
          />
        )}

        {activeTab === 'tasks' && (
          <TasksPanel
            opportunity={opportunity}
            token={token}
            onUpdate={setOpportunity}
          />
        )}

        {activeTab === 'documents' && (
          <DocumentsTab opportunity={opportunity} token={token} onUpdate={setOpportunity} phaseColor={phaseColor} />
        )}
      </div>

      {/* Phase change modal */}
      {showPhaseModal && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px', overflowY: 'auto',
          zIndex: 9999,
        }}>
          <div style={{
            background: '#0A0E16',
            border: `1px solid ${PHASE_COLOR[pendingPhase] || '#E27B58'}55`,
            borderRadius: '12px',
            padding: '28px',
            width: '100%',
            maxWidth: 400,
            maxHeight: 'calc(100dvh - 32px)',
            overflowY: 'auto',
          }}>
            <h3 style={{ margin: '0 0 8px', color: PHASE_COLOR[pendingPhase], textTransform: 'uppercase', letterSpacing: '1px', fontSize: '1rem' }}>
              Cambiar fase → {PHASE_LABELS[pendingPhase]}
            </h3>
            <p style={{ color: '#888', fontSize: '0.82rem', marginBottom: '16px' }}>Añade una nota opcional sobre este cambio de fase.</p>
            <textarea
              placeholder="Nota sobre el cambio de fase..."
              value={phaseNote}
              onChange={e => setPhaseNote(e.target.value)}
              style={{
                width: '100%',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '6px',
                color: '#F0F0F0',
                padding: '10px',
                fontSize: '0.85rem',
                resize: 'vertical',
                minHeight: '70px',
                outline: 'none',
                boxSizing: 'border-box',
                marginBottom: '16px',
              }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowPhaseModal(false)} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#888', padding: '9px 18px', cursor: 'pointer', fontSize: '0.82rem' }}>
                Cancelar
              </button>
              <button onClick={confirmPhaseChange} disabled={advancingPhase} style={{ background: PHASE_COLOR[pendingPhase] || '#E27B58', border: 'none', borderRadius: '6px', color: 'white', padding: '9px 18px', cursor: 'pointer', fontWeight: '700', fontSize: '0.82rem' }}>
                {advancingPhase ? 'Guardando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Helpers

const Section = ({ title, color, children }) => (
  <div style={{ marginBottom: '28px' }}>
    <h2 style={{
      margin: '0 0 14px',
      fontSize: '0.75rem',
      color,
      textTransform: 'uppercase',
      letterSpacing: '2px',
      fontWeight: '900',
      borderBottom: `1px solid ${color}33`,
      paddingBottom: '8px',
    }}>
      {title}
    </h2>
    {children}
  </div>
);

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', gap: '12px', marginBottom: '8px', padding: '8px 10px', background: 'rgba(255,255,255,0.02)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.04)' }}>
    <span style={{ fontSize: '0.7rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', minWidth: 100, flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: '0.85rem', color: '#CCC' }}>{value}</span>
  </div>
);

// Upload a file to an opportunity; returns the updated opportunity or throws.
const uploadAttachment = async (opportunityId, token, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await axios.post(
    `${getApiUrl()}/opportunities/${opportunityId}/attachments`,
    formData,
    { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
  );
  return data;
};

const UploadButton = ({ label, accept, phaseColor, uploading, onFile, Icon = Paperclip }) => (
  <label style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    padding: '12px',
    border: `1px dashed ${phaseColor}44`,
    borderRadius: '8px',
    color: phaseColor,
    cursor: uploading ? 'not-allowed' : 'pointer',
    fontSize: '0.82rem',
    opacity: uploading ? 0.6 : 1,
  }}>
    {uploading ? <Loader size={14} className="spin" /> : <Icon size={14} />}
    {uploading ? 'Subiendo…' : label}
    <input type="file" accept={accept} style={{ display: 'none' }} onChange={onFile} disabled={uploading} />
  </label>
);

const DocumentsTab = ({ opportunity, token, onUpdate, phaseColor }) => {
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(null);
  const attachments = opportunity.attachments || [];

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      onUpdate(await uploadAttachment(opportunity._id, token, file));
    } catch (err) {
      alert(err.response?.data?.message || 'Error al subir el archivo (solo JPG/PNG/PDF, máx 5MB).');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (attId) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    setBusy(attId);
    try {
      const { data } = await axios.delete(
        `${getApiUrl()}/opportunities/${opportunity._id}/attachments/${attId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(data);
    } catch (err) {
      console.error('Delete error', err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      {attachments.length === 0 && (
        <p style={{ color: '#444', fontSize: '0.8rem', fontStyle: 'italic', marginBottom: '16px' }}>No hay documentos adjuntos.</p>
      )}
      {attachments.map((att) => (
        <div key={att._id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', marginBottom: '8px' }}>
          {isImage(att)
            ? <img src={fileUrl(att.url)} alt={att.name} style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
            : <div style={{ width: 42, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.04)', borderRadius: '4px', flexShrink: 0 }}><FileText size={16} color="#888" /></div>}
          <a href={fileUrl(att.url)} target="_blank" rel="noopener noreferrer" style={{ color: '#00CEC9', fontSize: '0.85rem', textDecoration: 'none', flex: 1, wordBreak: 'break-all' }}>{att.name}</a>
          <button onClick={() => handleDelete(att._id)} disabled={busy === att._id} title="Eliminar" style={{ background: 'transparent', border: 'none', color: busy === att._id ? '#555' : '#E74C3C', cursor: 'pointer', display: 'flex', padding: '6px' }}>
            {busy === att._id ? <Loader size={13} className="spin" /> : <Trash2 size={13} />}
          </button>
        </div>
      ))}
      <div style={{ marginTop: '8px' }}>
        <UploadButton label="Subir documento (JPG/PNG/PDF)" phaseColor={phaseColor} uploading={uploading} onFile={handleUpload} />
      </div>
    </div>
  );
};

// ── Generic metadata form (drives the General / Local / Contract tabs) ─────────
const fieldBoxStyle = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '8px',
  color: '#DDD',
  padding: '9px 10px',
  fontSize: '0.82rem',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const FieldLabel = ({ field, value }) => (
  <span style={{ fontSize: '0.7rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', justifyContent: 'space-between' }}>
    <span>{field.label}</span>
    {field.maxLength ? <span style={{ color: '#3a3a3a' }}>{String(value || '').length}/{field.maxLength}</span> : null}
  </span>
);

const SelectField = ({ label, value, onChange, options }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <span style={{ fontSize: '0.7rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      style={{
        ...fieldBoxStyle,
        // Native selects paint their own background; force a solid dark control
        // and dark popup (color-scheme) so the light text stays readable.
        backgroundColor: '#0A0E16',
        color: '#DDD',
        colorScheme: 'dark',
        cursor: 'pointer',
      }}
    >
      <option value="" style={{ background: '#0A0E16', color: '#888' }}>Seleccione un valor…</option>
      {options.map(o => <option key={o} value={o} style={{ background: '#0A0E16', color: '#DDD' }}>{o}</option>)}
    </select>
  </label>
);

const MetaField = ({ field, value, onChange }) => {
  if (field.type === 'select') {
    return <SelectField label={field.label} value={value} options={field.options} onChange={onChange} />;
  }
  const common = {
    value: value ?? '',
    onChange: e => onChange(e.target.value),
    maxLength: field.maxLength,
    placeholder: field.placeholder || '',
  };
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <FieldLabel field={field} value={value} />
      {field.type === 'textarea'
        ? <textarea rows={2} {...common} style={{ ...fieldBoxStyle, resize: 'vertical', minHeight: '42px' }} />
        : <input type="text" inputMode={field.type === 'number' ? 'decimal' : undefined} {...common} style={fieldBoxStyle} />}
    </label>
  );
};

const SaveBar = ({ saving, saved, phaseColor, onSave, label = 'Guardar' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
    <button
      onClick={onSave}
      disabled={saving}
      style={{ background: phaseColor, border: 'none', borderRadius: '8px', color: 'white', padding: '10px 18px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, opacity: saving ? 0.6 : 1 }}
    >
      {saving ? 'Guardando…' : label}
    </button>
    {saved && !saving && <span style={{ color: '#27AE60', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px' }}><Check size={13} /> Guardado</span>}
  </div>
);

const MetadataForm = ({ opportunity, token, onUpdate, phaseColor, group, sections }) => {
  const keys = sections.flatMap(s => s.fields.map(f => f.key));
  const buildValues = () => Object.fromEntries(keys.map(k => [k, opportunity.metadata?.[group]?.[k] ?? '']));
  const [values, setValues] = useState(buildValues);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setValues(Object.fromEntries(keys.map(k => [k, opportunity.metadata?.[group]?.[k] ?? ''])));
    setSaved(false);
    // eslint-disable-next-line
  }, [opportunity, group]);

  const setField = (k, v) => { setValues(prev => ({ ...prev, [k]: v })); setSaved(false); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const nextMetadata = {
        ...(opportunity.metadata || {}),
        [group]: { ...(opportunity.metadata?.[group] || {}), ...values },
      };
      const { data } = await axios.put(
        `${getApiUrl()}/opportunities/${opportunity._id}`,
        { metadata: nextMetadata },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(data);
      setSaved(true);
    } catch (err) {
      console.error('Error saving', group, err);
      alert('No se pudo guardar. Revisa la conexión e inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {sections.map((sec, si) => (
        <Section key={si} title={sec.title} color={phaseColor}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
            {sec.fields.map(f => (
              <div key={f.key} style={{ gridColumn: f.span === 2 ? '1 / -1' : 'auto' }}>
                <MetaField field={f} value={values[f.key]} onChange={v => setField(f.key, v)} />
              </div>
            ))}
          </div>
        </Section>
      ))}
      <SaveBar saving={saving} saved={saved} phaseColor={phaseColor} onSave={handleSave} />
    </div>
  );
};

// Local tab = surface fields + a photo gallery with a representative image.
const LocalTab = ({ opportunity, token, onUpdate, phaseColor }) => (
  <div>
    <MetadataForm opportunity={opportunity} token={token} onUpdate={onUpdate} phaseColor={phaseColor} group="local" sections={LOCAL_SECTIONS} />
    <div style={{ height: '20px' }} />
    <PhotosBlock opportunity={opportunity} token={token} onUpdate={onUpdate} phaseColor={phaseColor} />
  </div>
);

const PhotosBlock = ({ opportunity, token, onUpdate, phaseColor }) => {
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(null);
  const images = (opportunity.attachments || []).filter(isImage);
  const rep = opportunity.metadata?.local?.representativeImage || '';

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      onUpdate(await uploadAttachment(opportunity._id, token, file));
    } catch (err) {
      alert(err.response?.data?.message || 'Error al subir la imagen (solo JPG/PNG, máx 5MB).');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const setRepresentative = async (url) => {
    setBusy(url);
    try {
      const nextMetadata = {
        ...(opportunity.metadata || {}),
        local: { ...(opportunity.metadata?.local || {}), representativeImage: url === rep ? '' : url },
      };
      const { data } = await axios.put(
        `${getApiUrl()}/opportunities/${opportunity._id}`,
        { metadata: nextMetadata },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(data);
    } catch (err) {
      console.error('Error setting representative image', err);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (attId) => {
    if (!window.confirm('¿Eliminar esta imagen?')) return;
    setBusy(attId);
    try {
      const { data } = await axios.delete(
        `${getApiUrl()}/opportunities/${opportunity._id}/attachments/${attId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(data);
    } catch (err) {
      console.error('Error deleting image', err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Section title="Fotos" color={phaseColor}>
      {images.length === 0 && (
        <p style={{ color: '#444', fontSize: '0.8rem', fontStyle: 'italic', marginBottom: '12px' }}>
          Sin fotos. Sube imágenes del local y marca una como representativa con la estrella.
        </p>
      )}
      {images.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px', marginBottom: '14px' }}>
          {images.map((att) => {
            const isRep = att.url === rep;
            return (
              <div key={att._id} style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: `2px solid ${isRep ? phaseColor : 'rgba(255,255,255,0.08)'}` }}>
                <img src={fileUrl(att.url)} alt={att.name} style={{ width: '100%', height: '110px', objectFit: 'cover', display: 'block' }} />
                {isRep && (
                  <span style={{ position: 'absolute', top: 6, left: 6, background: phaseColor, color: 'white', fontSize: '0.6rem', fontWeight: 700, padding: '2px 6px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Representativa
                  </span>
                )}
                <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: '4px' }}>
                  <button onClick={() => setRepresentative(att.url)} disabled={busy === att.url} title={isRep ? 'Quitar como representativa' : 'Marcar como representativa'}
                    style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '4px', color: isRep ? phaseColor : 'white', cursor: 'pointer', display: 'flex', padding: '4px' }}>
                    <Star size={13} fill={isRep ? phaseColor : 'none'} />
                  </button>
                  <button onClick={() => remove(att._id)} disabled={busy === att._id} title="Eliminar"
                    style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '4px', color: '#E74C3C', cursor: 'pointer', display: 'flex', padding: '4px' }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <UploadButton label="Adjuntar imagen (JPG/PNG)" accept="image/*" phaseColor={phaseColor} uploading={uploading} onFile={handleUpload} Icon={ImageIcon} />
    </Section>
  );
};

const DataTab = ({ opportunity, token, onUpdate, phaseColor }) => {
  const [saving, setSaving] = useState(false);
  const [address, setAddress] = useState(opportunity?.address || '');
  const [cadastre, setCadastre] = useState({
    reference: opportunity?.metadata?.cadastre?.reference || '',
    useType: opportunity?.metadata?.cadastre?.useType || '',
    builtArea: opportunity?.metadata?.cadastre?.builtArea || '',
    plotArea: opportunity?.metadata?.cadastre?.plotArea || '',
    notes: opportunity?.metadata?.cadastre?.notes || ''
  });
  const [registry, setRegistry] = useState({
    registryNumber: opportunity?.metadata?.propertyRegistry?.registryNumber || '',
    volume: opportunity?.metadata?.propertyRegistry?.volume || '',
    book: opportunity?.metadata?.propertyRegistry?.book || '',
    folio: opportunity?.metadata?.propertyRegistry?.folio || '',
    propertyId: opportunity?.metadata?.propertyRegistry?.propertyId || '',
    notes: opportunity?.metadata?.propertyRegistry?.notes || ''
  });
  const [cadastralLoading, setCadastralLoading] = useState(false);
  const [gfModal, setGfModal] = useState(null); // { units, allShown }

  const handleCadastralLookup = async () => {
    const q = address.trim();
    if (!q || cadastralLoading) return;
    setCadastralLoading(true);
    try {
      // 1) Address → cadastral reference (Catastro provider).
      const geo = await axios.post(
        `${getApiUrl()}/location/geocode-catastro`,
        { address: q },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const rc14 = geo.data.referenciaCatastral14;
      const rc20 = geo.data.referenciaCatastral;
      setCadastre(prev => ({ ...prev, reference: rc14 }));

      // 2) Reference → cadastral detail → ground-floor units.
      const det = await axios.get(`${getApiUrl()}/location/catastral`, {
        params: { rc: rc14 },
        headers: { Authorization: `Bearer ${token}` },
      });
      const inmuebles = det.data.inmuebles || [];
      let units = groundFloorUnits(inmuebles);
      let allShown = false;
      if (units.length === 0 && inmuebles.length > 0) {
        allShown = true;
        units = inmuebles.map(inm => ({
          rc: inm.referenciaCatastral,
          uso: inm.uso || '',
          direccion: inm.direccion || '',
          m2: Number(inm.superficieConstruccionM2) || 0,
          puertas: '',
        }));
      }

      if (units.length === 0) {
        // No detail to pick from: at least keep the full 20-char reference in notes.
        setCadastre(prev => ({ ...prev, notes: prependNote(prev.notes, `Referencia catastral (inmueble): ${rc20}`) }));
      } else {
        setGfModal({ units, allShown });
      }
    } catch (e) {
      alert(e.response?.data?.message || 'Error consultando el Catastro.');
    } finally {
      setCadastralLoading(false);
    }
  };

  const handleSelectUnit = (u) => {
    setCadastre(prev => ({
      ...prev,
      builtArea: u.m2 ? String(u.m2) : prev.builtArea,
      useType: prev.useType || u.uso || '',
      notes: prependNote(prev.notes, `Inmueble (planta baja): ${u.rc} — ${u.m2} m²${u.uso ? ' — ' + u.uso : ''}`),
    }));
    setGfModal(null);
  };

  useEffect(() => {
    setAddress(opportunity?.address || '');
    setCadastre({
      reference: opportunity?.metadata?.cadastre?.reference || '',
      useType: opportunity?.metadata?.cadastre?.useType || '',
      builtArea: opportunity?.metadata?.cadastre?.builtArea || '',
      plotArea: opportunity?.metadata?.cadastre?.plotArea || '',
      notes: opportunity?.metadata?.cadastre?.notes || ''
    });
    setRegistry({
      registryNumber: opportunity?.metadata?.propertyRegistry?.registryNumber || '',
      volume: opportunity?.metadata?.propertyRegistry?.volume || '',
      book: opportunity?.metadata?.propertyRegistry?.book || '',
      folio: opportunity?.metadata?.propertyRegistry?.folio || '',
      propertyId: opportunity?.metadata?.propertyRegistry?.propertyId || '',
      notes: opportunity?.metadata?.propertyRegistry?.notes || ''
    });
  }, [opportunity]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const nextMetadata = {
        ...(opportunity.metadata || {}),
        cadastre,
        propertyRegistry: registry
      };
      const { data } = await axios.put(
        `${getApiUrl()}/opportunities/${opportunity._id}`,
        { address: address.trim(), metadata: nextMetadata },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(data);
    } catch (err) {
      console.error('Error saving data', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Address — first and prominent */}
      <Section title="Dirección" color={phaseColor}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontSize: '0.7rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Dirección completa</span>
          <input
            value={address}
            onChange={e => setAddress(e.target.value)}
            placeholder="Calle, número, ciudad..."
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: `1px solid ${phaseColor}55`,
              borderRadius: '8px',
              color: '#DDD',
              padding: '11px 12px',
              fontSize: '0.9rem',
              outline: 'none',
              width: '100%',
              boxSizing: 'border-box',
            }}
          />
        </label>
      </Section>

      <Section title="Datos de catastro" color={phaseColor}>
        <FormGrid>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '0.7rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Referencia catastral</span>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                value={cadastre.reference}
                onChange={e => setCadastre(prev => ({ ...prev, reference: e.target.value }))}
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '8px',
                  color: '#DDD',
                  padding: '9px 10px',
                  paddingRight: (address.trim() && !cadastre.reference) ? '36px' : '10px',
                  fontSize: '0.82rem',
                  outline: 'none',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              />
              {address.trim() && !cadastre.reference && (
                <button
                  type="button"
                  onClick={handleCadastralLookup}
                  disabled={cadastralLoading}
                  title="Buscar referencia catastral a partir de la dirección"
                  style={{
                    position: 'absolute', right: '6px',
                    background: 'transparent', border: 'none',
                    color: phaseColor, cursor: cadastralLoading ? 'wait' : 'pointer',
                    display: 'flex', alignItems: 'center', padding: '4px',
                  }}
                >
                  {cadastralLoading ? <Loader size={15} /> : <Search size={15} />}
                </button>
              )}
            </div>
          </label>
          <InputField label="Uso" value={cadastre.useType} onChange={v => setCadastre(prev => ({ ...prev, useType: v }))} />
          <InputField label="Superficie construida (m2)" value={cadastre.builtArea} onChange={v => setCadastre(prev => ({ ...prev, builtArea: v }))} />
          <InputField label="Superficie parcela (m2)" value={cadastre.plotArea} onChange={v => setCadastre(prev => ({ ...prev, plotArea: v }))} />
        </FormGrid>
        <TextAreaField label="Notas de catastro" value={cadastre.notes} onChange={v => setCadastre(prev => ({ ...prev, notes: v }))} />
      </Section>

      <Section title="Datos de registro de la propiedad" color={phaseColor}>
        <FormGrid>
          <InputField label="Numero de finca" value={registry.registryNumber} onChange={v => setRegistry(prev => ({ ...prev, registryNumber: v }))} />
          <InputField label="Tomo" value={registry.volume} onChange={v => setRegistry(prev => ({ ...prev, volume: v }))} />
          <InputField label="Libro" value={registry.book} onChange={v => setRegistry(prev => ({ ...prev, book: v }))} />
          <InputField label="Folio" value={registry.folio} onChange={v => setRegistry(prev => ({ ...prev, folio: v }))} />
          <InputField label="ID registral" value={registry.propertyId} onChange={v => setRegistry(prev => ({ ...prev, propertyId: v }))} />
        </FormGrid>
        <TextAreaField label="Notas del registro" value={registry.notes} onChange={v => setRegistry(prev => ({ ...prev, notes: v }))} />
      </Section>

      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          background: phaseColor,
          border: 'none',
          borderRadius: '8px',
          color: 'white',
          padding: '10px 16px',
          cursor: saving ? 'not-allowed' : 'pointer',
          fontWeight: 700,
          opacity: saving ? 0.6 : 1
        }}
      >
        {saving ? 'Guardando...' : 'Guardar'}
      </button>

      {/* Ground-floor unit selector (Catastro) */}
      {gfModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#0A0E16', border: `1px solid ${phaseColor}55`, borderRadius: '12px', padding: '24px', width: '100%', maxWidth: 540, maxHeight: '80vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 6px', color: phaseColor, textTransform: 'uppercase', letterSpacing: '1px', fontSize: '1rem' }}>
              Selecciona el local en planta baja
            </h3>
            <p style={{ color: '#888', fontSize: '0.8rem', marginBottom: '16px' }}>
              {gfModal.allShown
                ? 'No se detectaron locales en planta baja; mostrando todos los inmuebles de la finca.'
                : 'Elige el inmueble que ocupará la oportunidad. Se rellenará la superficie y se guardará su referencia completa en las notas.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {gfModal.units.map((u, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleSelectUnit(u)}
                  style={{
                    textAlign: 'left', width: '100%',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '8px', padding: '12px', cursor: 'pointer',
                  }}
                  onMouseOver={e => { e.currentTarget.style.borderColor = `${phaseColor}88`; }}
                  onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                >
                  <div style={{ color: '#F0F0F0', fontSize: '0.85rem', fontWeight: 600, marginBottom: '3px' }}>
                    {u.direccion || u.rc}
                  </div>
                  <div style={{ color: phaseColor, fontSize: '0.82rem' }}>
                    {u.m2 ? `${u.m2} m²` : 'Sin superficie'}{u.uso ? ` · ${u.uso}` : ''}{u.puertas ? ` · Pt ${u.puertas}` : ''}
                  </div>
                  <div style={{ color: '#555', fontSize: '0.68rem', fontFamily: 'monospace', marginTop: '4px' }}>{u.rc}</div>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '18px' }}>
              <button
                type="button"
                onClick={() => setGfModal(null)}
                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#888', padding: '9px 18px', cursor: 'pointer', fontSize: '0.82rem' }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Editable PASTA V scores card (used in the Summary tab).
const PastaScoresCard = ({ opportunity, token, onUpdate, phaseColor }) => {
  const emptyScores = () => Object.fromEntries(
    PASTA_FIELDS.map(f => [f.key, String(opportunity?.pastaScores?.[f.key] ?? '')])
  );
  const [scores, setScores] = useState(emptyScores);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setScores(Object.fromEntries(
      PASTA_FIELDS.map(f => [f.key, String(opportunity?.pastaScores?.[f.key] ?? '')])
    ));
  }, [opportunity]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const out = {};
      PASTA_FIELDS.forEach(f => {
        const n = Number(scores[f.key]);
        if (!isNaN(n) && n > 0) out[f.key] = Math.min(10, Math.max(1, n));
      });
      const { data } = await axios.put(
        `${getApiUrl()}/opportunities/${opportunity._id}`,
        { pastaScores: out },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(data);
    } catch (err) {
      console.error('Error saving PASTA scores', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="PASTA V Scores (1–10)" color={phaseColor}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: '10px' }}>
        {PASTA_FIELDS.map(f => {
          const val = scores[f.key];
          const n = Number(val);
          const hasScore = val !== '' && !isNaN(n) && n > 0;
          const pct = hasScore ? (n / 10) * 100 : 0;
          const barColor = n >= 7 ? '#27AE60' : n >= 4 ? '#F39C12' : n > 0 ? '#E74C3C' : '#333';
          return (
            <div key={f.key} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '10px', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ fontSize: '0.65rem', color: '#666', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.5px' }}>{f.label}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.05)', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: '2px', transition: 'width 0.2s' }} />
                </div>
                <span style={{ fontSize: '0.9rem', fontWeight: '700', color: hasScore ? '#F0F0F0' : '#333', fontFamily: 'monospace', minWidth: 18, textAlign: 'right' }}>
                  {hasScore ? n : '–'}
                </span>
              </div>
              <input
                type="number"
                min="1"
                max="10"
                value={val}
                onChange={e => setScores(prev => ({ ...prev, [f.key]: e.target.value }))}
                placeholder="0"
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  color: '#CCC',
                  padding: '6px 8px',
                  fontSize: '0.82rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          );
        })}
      </div>
      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          marginTop: '12px',
          background: phaseColor,
          border: 'none',
          borderRadius: '8px',
          color: 'white',
          padding: '9px 16px',
          cursor: saving ? 'not-allowed' : 'pointer',
          fontWeight: 700,
          opacity: saving ? 0.6 : 1,
        }}
      >
        {saving ? 'Guardando...' : 'Guardar scores'}
      </button>
    </Section>
  );
};

const FormGrid = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px', marginBottom: '10px' }}>
    {children}
  </div>
);

const InputField = ({ label, value, onChange }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <span style={{ fontSize: '0.7rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '8px',
        color: '#DDD',
        padding: '9px 10px',
        fontSize: '0.82rem',
        outline: 'none'
      }}
    />
  </label>
);

const TextAreaField = ({ label, value, onChange }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
    <span style={{ fontSize: '0.7rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      rows={3}
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '8px',
        color: '#DDD',
        padding: '9px 10px',
        fontSize: '0.82rem',
        outline: 'none',
        resize: 'vertical'
      }}
    />
  </label>
);

// Need to import Paperclip in DocumentsTab scope — already imported at top
// Need axios — already imported at top

export default OpportunityDetailPage;
